import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Tag, Text } from '@nimbus-ds/components';
import { useParams } from 'react-router-dom';
import { RefundAttemptStatus } from '@tiendanube/common/src/domains/payments/transactions/RefundAttemptDto';
import { OPTIONAL_CANCEL_REFUNDED_ORDER_TAG } from 'App/featuresFlags';
import { useHasTags, useNavegate } from 'App/hooks';
import { HeaderContent, HeaderTop, IonPageStratus } from 'commons/components';
import { useForm, useModal } from 'commons/hooks';
import {
  RefundOrderPageConfirmationModal,
  RefundOrderPageSkeleton,
} from './components';
import CurrentPage from './components/CurrentPage';
import {
  RefundUnavailableMessage,
  RefundStatus,
} from './components/RefundUnavailableMessage';
import { useSteps } from './hooks';
import UseTranslationOrders from '../../../useTranslationOrders';
import { useOrderDetails } from '../../hooks';
import { useCanBeRefunded, useRefundOrder } from '../hooks';
import {
  RefundAttributes,
  RefundAttributesTypes,
} from '../hooks/useRefundOrder/useRefundOrder';

function RefundOrderPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const t = UseTranslationOrders();
  const { orderDetails, isLoading: isOrderDetailsLoading } =
    useOrderDetails(id);
  const {
    isLoading: isRefundOrderLoading,
    isSuccess: isRefundOrderSuccess,
    isError,
    refund,
    refundOrderErrorCode,
  } = useRefundOrder(id);
  const { goBack, goTo } = useNavegate();
  const { canBeRefundedThroughRefundForm, canBePartiallyRefunded } =
    useCanBeRefunded(orderDetails);
  const [closeAlert, setCloseAlert] = useState<boolean>(false);
  const [isShow, openModal, closeModal] = useModal();
  const useStepsResponse = useSteps(goBack);

  const isPartialRefundEnabled =
    canBePartiallyRefunded() &&
    (!orderDetails || orderDetails.payment.supportsPartialRefund);

  const refundableAmount =
    orderDetails?.payment.refundableAmount?.value ?? '0.00';
  const hasRefundAttemptsInProgress =
    orderDetails?.payment.refundAttempts?.some(
      (attempt) => attempt.status.code === RefundAttemptStatus.IN_PROGRESS,
    ) ?? false;

  const [hasCancelRefundedOrderOptionalTag] = useHasTags(
    OPTIONAL_CANCEL_REFUNDED_ORDER_TAG,
  );
  const initialValues: RefundAttributes = useMemo(
    () => ({
      type: isPartialRefundEnabled
        ? RefundAttributesTypes.PARTIAL_BY_VALUE
        : RefundAttributesTypes.TOTAL,
      value: isPartialRefundEnabled ? '' : refundableAmount,
      returnsStock: false,
      notifiesConsumer: false,
      cancelsOrder: !hasCancelRefundedOrderOptionalTag,
    }),
    [
      isPartialRefundEnabled,
      refundableAmount,
      hasCancelRefundedOrderOptionalTag,
    ],
  );

  const { values, handleOnChange, handleOnSubmit, isDirty } = useForm<
    RefundAttributes,
    RefundAttributes
  >({
    initialValues,
    onSubmit: async (values) => {
      if (!orderDetails) return;
      return refund(values, orderDetails);
    },
  });

  const isFormFilled = () => !!values.type && !!values.value;

  const isInputValid = () => {
    const amountToRefund = Number(values.value);
    return amountToRefund > 0 && amountToRefund <= Number(refundableAmount);
  };

  const disableButton = () =>
    (isRefundOrderLoading && !isDirty) || !isFormFilled() || !isInputValid();

  useEffect(() => {
    if (isError || isRefundOrderSuccess) {
      closeModal();
    }
    if (isRefundOrderSuccess) {
      goTo(`/orders/${id}`);
    }
  }, [isError, isRefundOrderSuccess, goBack, closeModal, goTo, id]);

  const showAlert = isError && !closeAlert;

  function getRefundStatus(): RefundStatus {
    if (orderDetails?.payment.status === 'refunded') {
      return RefundStatus.REFUNDED;
    }
    if (hasRefundAttemptsInProgress) {
      return RefundStatus.IN_PROGRESS;
    } else {
      return RefundStatus.NOT_ALLOWED;
    }
  }

  return (
    <IonPageStratus
      width="small"
      headerTop={<HeaderTop navigation={{ onClick: goBack }} />}
      headerContent={
        <HeaderContent
          title={t('refund.title', { orderId: orderDetails?.number })}
        />
      }
    >
      <>
        {isOrderDetailsLoading && <RefundOrderPageSkeleton />}

        {((!isOrderDetailsLoading && !canBeRefundedThroughRefundForm()) ||
          hasRefundAttemptsInProgress) && (
          <RefundUnavailableMessage
            orderId={id}
            refundStatus={getRefundStatus()}
          />
        )}

        {!isOrderDetailsLoading &&
          !hasRefundAttemptsInProgress &&
          canBeRefundedThroughRefundForm() &&
          orderDetails && (
            <>
              <Tag appearance="neutral">
                {t('refund.stepsInfo', {
                  currentStep: useStepsResponse.currentStep,
                  totalSteps: useStepsResponse.totalSteps,
                })}
              </Tag>
              {isError && (
                <Alert
                  onRemove={() => setCloseAlert(true)}
                  appearance="danger"
                  title={t(`refund.errors.${refundOrderErrorCode}.title`)}
                  show={showAlert}
                >
                  <Text>
                    {t(`refund.errors.${refundOrderErrorCode}.description`)}
                  </Text>
                </Alert>
              )}
              <CurrentPage
                orderDetails={orderDetails}
                values={values}
                onDetailsChange={handleOnChange}
                onConfirm={openModal}
                useSteps={useStepsResponse}
                disableButton={disableButton}
                disableCancelOrderCheckbox={!hasCancelRefundedOrderOptionalTag}
              />
              <RefundOrderPageConfirmationModal
                onConfirm={handleOnSubmit}
                closeModal={closeModal}
                open={isShow}
                isLoading={isRefundOrderLoading}
              />
            </>
          )}
      </>
    </IonPageStratus>
  );
}

export default RefundOrderPage;
